import React, { useEffect, useState, useMemo } from "react";
import {
  BarChart3,
  Eye,
  Users,
  MousePointerClick,
  ShoppingBag,
  BookOpen,
  BookMarked,
  CalendarDays,
  ListVideo,
  Smartphone,
  Monitor,
  Tablet,
  RefreshCw,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * Admin Analytics dashboard, privacy-first, native, no third-party cookies.
 * Endpoint: GET /api/admin/analytics/summary?range=today|7d|30d
 *
 * Renderiza:
 *   - Filtros de período (Hoje, 7d, 30d)
 *   - 8 KPI cards (acessos hoje/janela, cliques livro, planos iniciados, sermões, séries, devocional, vídeos)
 *   - Gráfico de acessos por hora do dia (0..23)
 *   - Gráfico de acessos por dia
 *   - Split de dispositivos (mobile / desktop / tablet)
 *   - Top páginas + top eventos
 */
const RANGES = [
  { key: "today", label: "Hoje" },
  { key: "7d", label: "7 dias" },
  { key: "30d", label: "30 dias" },
];

export default function AnalyticsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("7d");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(`/admin/analytics/summary?range=${range}`)
      .then(({ data }) => mounted && setData(data))
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [range, refreshKey]);

  return (
    <section
      id="analytics"
      data-testid="admin-analytics-section"
      className="mt-14 bg-white border border-[#0B1E36]/10 p-8"
    >
      <Header
        range={range}
        setRange={setRange}
        onRefresh={() => setRefreshKey((k) => k + 1)}
      />

      {loading ? (
        <SkeletonBody />
      ) : !data ? (
        <p
          data-testid="admin-analytics-empty"
          className="text-sm text-[#6B7280] italic mt-6"
        >
          Sem dados suficientes ainda. Os números aparecerão conforme os
          visitantes acessarem a landing page.
        </p>
      ) : (
        <Body data={data} range={range} />
      )}
    </section>
  );
}

function Header({ range, setRange, onRefresh }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
      <div className="flex items-center gap-3 text-[#C5A059]">
        <BarChart3 size={16} strokeWidth={1.5} />
        <h2 className="text-[0.7rem] uppercase tracking-[0.28em] font-medium">
          Analytics
        </h2>
        <span
          aria-hidden
          className="text-[0.6rem] tracking-[0.22em] text-[#0B1E36]/40 uppercase ml-2 border border-[#0B1E36]/10 px-2 py-0.5"
        >
          LGPD · sem cookies · anônimo
        </span>
      </div>
      <div className="flex items-center gap-2">
        <div
          data-testid="admin-analytics-range"
          className="inline-flex border border-[#0B1E36]/15"
        >
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              data-testid={`admin-analytics-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`text-xs uppercase tracking-[0.22em] px-4 py-2 transition-colors ${
                range === r.key
                  ? "bg-[#0B1E36] text-white"
                  : "text-[#0B1E36]/70 hover:bg-[#FAF8F4]"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={onRefresh}
          data-testid="admin-analytics-refresh"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#0B1E36]/70 hover:text-[#C5A059] px-2 py-2"
          title="Recarregar"
        >
          <RefreshCw size={12} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}

function SkeletonBody() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 bg-[#F5F7FA] animate-pulse" />
        ))}
      </div>
      <div className="h-40 bg-[#F5F7FA] animate-pulse" />
      <div className="h-40 bg-[#F5F7FA] animate-pulse" />
    </div>
  );
}

function Body({ data, range }) {
  const rangeLabel =
    RANGES.find((r) => r.key === range)?.label.toLowerCase() || `${data.range_days}d`;

  return (
    <div className="space-y-12">
      {/* --- KPI cards --- */}
      <div
        data-testid="admin-analytics-kpis"
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
      >
        <KPI
          icon={Eye}
          label="Acessos hoje"
          value={data.page_views_today}
          sub={`${data.sessions_today} sessões únicas`}
          testId="kpi-views-today"
        />
        <KPI
          icon={Users}
          label={`Sessões · ${rangeLabel}`}
          value={data.unique_sessions}
          sub={`${data.page_views} page views`}
          testId="kpi-sessions-range"
        />
        <KPI
          icon={ShoppingBag}
          label="Cliques · livro"
          value={data.kpis?.click_buy_book || 0}
          testId="kpi-buy-book"
          highlight
        />
        <KPI
          icon={BookOpen}
          label="Planos iniciados"
          value={data.kpis?.subscribe_plan || 0}
          sub={`${data.kpis?.click_plan || 0} visitas`}
          testId="kpi-plans"
        />
        <KPI
          icon={BookMarked}
          label="Sermões abertos"
          value={data.kpis?.click_sermon || 0}
          testId="kpi-sermons"
        />
        <KPI
          icon={CalendarDays}
          label="Séries acessadas"
          value={data.kpis?.click_series || 0}
          testId="kpi-series"
        />
        <KPI
          icon={BookOpen}
          label="Devocional aberto"
          value={data.kpis?.devotional_open || 0}
          testId="kpi-devotional"
        />
        <KPI
          icon={ListVideo}
          label="Vídeos abertos"
          value={data.kpis?.video_open || 0}
          testId="kpi-videos"
        />
      </div>

      {/* --- Hourly chart --- */}
      <HourlyChart data={data.hourly_page_views || []} />

      {/* --- Daily chart --- */}
      <DailyChart data={data.daily_page_views || []} />

      {/* --- Devices + top pages/events --- */}
      <div className="grid lg:grid-cols-3 gap-10">
        <DeviceSplit devices={data.devices || []} />
        <TopList
          title="Páginas mais visitadas"
          items={(data.top_pages || []).map((p) => ({
            label: p.page,
            count: p.count,
          }))}
          testId="analytics-top-pages"
          mono
        />
        <TopList
          title="Eventos mais acionados"
          items={(data.top_events || []).map((e) => ({
            label: e.event + (e.location ? ` · ${e.location}` : ""),
            count: e.count,
          }))}
          testId="analytics-top-events"
        />
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, testId, highlight }) {
  return (
    <div
      data-testid={testId}
      className={`px-5 py-5 border ${
        highlight
          ? "bg-[#C5A059]/8 border-[#C5A059]/30"
          : "bg-[#F5F7FA] border-[#0B1E36]/8"
      }`}
    >
      <div className="flex items-center gap-2 text-[#C5A059] mb-3">
        <Icon size={13} strokeWidth={1.6} />
        <span className="text-[0.62rem] uppercase tracking-[0.24em] text-[#0B1E36]/65 font-medium leading-tight">
          {label}
        </span>
      </div>
      <div className="font-serif-display text-[2rem] md:text-[2.15rem] text-[#0B1E36] leading-none tabular-nums">
        {(value || 0).toLocaleString("pt-BR")}
      </div>
      {sub && (
        <p className="mt-2 text-[0.65rem] uppercase tracking-[0.18em] text-[#0B1E36]/45">
          {sub}
        </p>
      )}
    </div>
  );
}

function HourlyChart({ data }) {
  const max = useMemo(
    () => Math.max(1, ...data.map((d) => d.count)),
    [data]
  );
  return (
    <div data-testid="analytics-hourly-chart">
      <p className="text-[0.68rem] uppercase tracking-[0.22em] text-[#0B1E36]/60 mb-4">
        Acessos por hora do dia (UTC)
      </p>
      <div className="flex items-end gap-1 h-28 border-b border-[#0B1E36]/10">
        {data.map((d) => (
          <div
            key={d.hour}
            title={`${String(d.hour).padStart(2, "0")}h: ${d.count}`}
            className="flex-1 bg-[#C5A059]/35 hover:bg-[#C5A059] transition-colors duration-200 min-h-[2px]"
            style={{
              height: `${Math.max(2, (d.count / max) * 100)}%`,
            }}
          />
        ))}
      </div>
      <div className="flex justify-between mt-2 text-[0.6rem] uppercase tracking-[0.18em] text-[#0B1E36]/45">
        {[0, 6, 12, 18, 23].map((h) => (
          <span key={h}>{String(h).padStart(2, "0")}h</span>
        ))}
      </div>
    </div>
  );
}

function DailyChart({ data }) {
  const max = useMemo(
    () => Math.max(1, ...data.map((d) => d.count)),
    [data]
  );
  if (data.length === 0) {
    return (
      <div>
        <p className="text-[0.68rem] uppercase tracking-[0.22em] text-[#0B1E36]/60 mb-4">
          Acessos por dia
        </p>
        <p className="text-sm text-[#6B7280] italic">
          Nenhuma page view registrada ainda.
        </p>
      </div>
    );
  }
  return (
    <div data-testid="analytics-daily-chart">
      <p className="text-[0.68rem] uppercase tracking-[0.22em] text-[#0B1E36]/60 mb-4">
        Acessos por dia
      </p>
      <div className="flex items-end gap-1 h-24 border-b border-[#0B1E36]/10">
        {data.map((d) => (
          <div
            key={d.date}
            title={`${d.date}: ${d.count} page views`}
            className="flex-1 bg-[#0B1E36]/75 hover:bg-[#0B1E36] transition-colors duration-200 min-h-[2px]"
            style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }}
          />
        ))}
      </div>
      <div className="flex justify-between mt-2 text-[0.6rem] uppercase tracking-[0.18em] text-[#0B1E36]/45">
        <span>{data[0].date}</span>
        <span>{data[data.length - 1].date}</span>
      </div>
    </div>
  );
}

function DeviceSplit({ devices }) {
  const total = devices.reduce((acc, d) => acc + d.count, 0) || 1;
  const iconMap = {
    mobile: Smartphone,
    desktop: Monitor,
    tablet: Tablet,
    unknown: Monitor,
  };
  return (
    <div data-testid="analytics-devices">
      <p className="text-[0.68rem] uppercase tracking-[0.22em] text-[#0B1E36]/60 mb-4">
        Dispositivos
      </p>
      {devices.length === 0 ? (
        <p className="text-sm text-[#6B7280] italic">Sem dados ainda.</p>
      ) : (
        <ul className="space-y-3">
          {devices.map((d) => {
            const pct = Math.round((d.count / total) * 100);
            const Ico = iconMap[d.device] || Monitor;
            return (
              <li key={d.device} className="flex items-center gap-3">
                <Ico
                  size={14}
                  strokeWidth={1.6}
                  className="text-[#C5A059] shrink-0"
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[0.78rem] text-[#0B1E36] capitalize font-medium">
                      {d.device}
                    </span>
                    <span className="text-[0.72rem] text-[#0B1E36]/70 tabular-nums">
                      {d.count.toLocaleString("pt-BR")} · {pct}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-[#F5F7FA]">
                    <div
                      className="h-full bg-[#C5A059]"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function TopList({ title, items, testId, mono }) {
  return (
    <div data-testid={testId}>
      <p className="text-[0.68rem] uppercase tracking-[0.22em] text-[#0B1E36]/60 mb-4">
        {title}
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-[#6B7280] italic">
          Nada registrado ainda neste período.
        </p>
      ) : (
        <ul className="divide-y divide-[#0B1E36]/8">
          {items.slice(0, 10).map((it, i) => (
            <li
              key={`${testId}-${i}`}
              className="flex items-center justify-between py-2.5 gap-3"
            >
              <span
                className={`text-[0.82rem] text-[#0B1E36] truncate flex-1 ${
                  mono ? "font-mono text-[0.8rem]" : ""
                }`}
                title={it.label}
              >
                {it.label}
              </span>
              <span className="text-[0.9rem] text-[#C5A059] tabular-nums font-medium">
                <MousePointerClick
                  size={10}
                  strokeWidth={1.7}
                  className="inline -mt-0.5 mr-1 opacity-60"
                />
                {it.count.toLocaleString("pt-BR")}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
